import * as THREE from 'three';
import { createMetalMaterial, createGlassMaterial, createFrostMaterial } from '../utils/materials';
import { tween, Easing } from '../utils/animate';
import { Shelf, ShelfData } from './Shelf';

export interface FreezerData {
  id: string;
  label: string;
  temperature: number;
  location: string;
  shelves: ShelfData[];
}

const FREEZER_WIDTH = 0.8;
const FREEZER_HEIGHT = 1.2;
const FREEZER_DEPTH = 0.6;
const DOOR_THICKNESS = 0.03;

export class Freezer {
  public mesh: THREE.Group;
  public freezerData: FreezerData;
  public shelves: Shelf[] = [];
  public isOpen = false;
  public isDimmed = false;
  private door: THREE.Group;
  private interiorLight: THREE.PointLight;
  private frostPlane: THREE.Mesh;
  private particleSystem: THREE.Points | null = null;
  private particleLifetimes: Float32Array | null = null;
  private labelSprite: THREE.Sprite;

  constructor(data: FreezerData) {
    this.freezerData = data;
    this.mesh = new THREE.Group();
    this.mesh.userData = { type: 'freezer', id: data.id, freezerRef: this };

    this.door = new THREE.Group();
    this.interiorLight = new THREE.PointLight(0x88bbff, 0, 1.5);
    this.frostPlane = new THREE.Mesh();
    this.labelSprite = new THREE.Sprite();

    this.buildBody();
    this.buildDoor();
    this.buildInterior();
    this.buildShelves();
    this.buildLabel();
    this.buildLEDDisplay();
  }

  private buildBody(): void {
    const metalMat = createMetalMaterial(0x8899aa);

    // Main body (without front face)
    const bodyGroup = new THREE.Group();

    // Back wall
    const back = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH, FREEZER_HEIGHT, 0.02),
      metalMat
    );
    back.position.z = -FREEZER_DEPTH / 2;
    back.castShadow = true;
    back.receiveShadow = true;
    bodyGroup.add(back);

    // Top
    const top = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH, 0.02, FREEZER_DEPTH),
      metalMat
    );
    top.position.y = FREEZER_HEIGHT / 2;
    top.castShadow = true;
    bodyGroup.add(top);

    // Bottom
    const bottom = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH, 0.02, FREEZER_DEPTH),
      metalMat
    );
    bottom.position.y = -FREEZER_HEIGHT / 2;
    bottom.receiveShadow = true;
    bodyGroup.add(bottom);

    // Left side
    const left = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, FREEZER_HEIGHT, FREEZER_DEPTH),
      metalMat
    );
    left.position.x = -FREEZER_WIDTH / 2;
    left.castShadow = true;
    bodyGroup.add(left);

    // Right side
    const right = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, FREEZER_HEIGHT, FREEZER_DEPTH),
      metalMat
    );
    right.position.x = FREEZER_WIDTH / 2;
    right.castShadow = true;
    bodyGroup.add(right);

    // Feet
    const footGeo = new THREE.CylinderGeometry(0.025, 0.03, 0.04, 8);
    const footMat = createMetalMaterial(0x555555);
    const footPositions = [
      [-FREEZER_WIDTH / 2 + 0.06, -FREEZER_HEIGHT / 2 - 0.02, FREEZER_DEPTH / 2 - 0.06],
      [FREEZER_WIDTH / 2 - 0.06, -FREEZER_HEIGHT / 2 - 0.02, FREEZER_DEPTH / 2 - 0.06],
      [-FREEZER_WIDTH / 2 + 0.06, -FREEZER_HEIGHT / 2 - 0.02, -FREEZER_DEPTH / 2 + 0.06],
      [FREEZER_WIDTH / 2 - 0.06, -FREEZER_HEIGHT / 2 - 0.02, -FREEZER_DEPTH / 2 + 0.06],
    ];
    for (const [x, y, z] of footPositions) {
      const foot = new THREE.Mesh(footGeo, footMat);
      foot.position.set(x, y, z);
      foot.castShadow = true;
      bodyGroup.add(foot);
    }

    bodyGroup.position.y = FREEZER_HEIGHT / 2 + 0.04;
    this.mesh.add(bodyGroup);
  }

  private buildDoor(): void {
    // Door pivot at left edge
    this.door.position.set(-FREEZER_WIDTH / 2, FREEZER_HEIGHT / 2 + 0.04, FREEZER_DEPTH / 2);

    // Glass door panel
    const glassMat = createGlassMaterial();
    const doorPanel = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH, FREEZER_HEIGHT, DOOR_THICKNESS),
      glassMat
    );
    doorPanel.position.x = FREEZER_WIDTH / 2;
    doorPanel.castShadow = true;
    this.door.add(doorPanel);

    // Door frame
    const frameMat = createMetalMaterial(0x99aabb);
    const frameThickness = 0.025;

    // Top frame
    const topFrame = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH, frameThickness, DOOR_THICKNESS + 0.01),
      frameMat
    );
    topFrame.position.set(FREEZER_WIDTH / 2, FREEZER_HEIGHT / 2 - frameThickness / 2, 0);
    this.door.add(topFrame);

    // Bottom frame
    const bottomFrame = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH, frameThickness, DOOR_THICKNESS + 0.01),
      frameMat
    );
    bottomFrame.position.set(FREEZER_WIDTH / 2, -FREEZER_HEIGHT / 2 + frameThickness / 2, 0);
    this.door.add(bottomFrame);

    // Handle
    const handleGeo = new THREE.BoxGeometry(0.02, 0.15, 0.03);
    const handle = new THREE.Mesh(handleGeo, createMetalMaterial(0xccddee));
    handle.position.set(FREEZER_WIDTH - 0.05, 0, DOOR_THICKNESS / 2 + 0.015);
    this.door.add(handle);

    // Frost overlay on glass
    this.frostPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(FREEZER_WIDTH * 0.9, FREEZER_HEIGHT * 0.9),
      createFrostMaterial()
    );
    this.frostPlane.position.set(FREEZER_WIDTH / 2, 0, DOOR_THICKNESS / 2 + 0.001);
    this.door.add(this.frostPlane);

    // Make door children clickable as freezer
    this.door.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.userData = { type: 'freezer', id: this.freezerData.id, freezerRef: this };
      }
    });

    this.mesh.add(this.door);
  }

  private buildInterior(): void {
    // Interior light
    this.interiorLight.position.set(0, FREEZER_HEIGHT * 0.8, 0);
    this.interiorLight.castShadow = false;
    const bodyYCenter = FREEZER_HEIGHT / 2 + 0.04;
    this.interiorLight.position.y = bodyYCenter + FREEZER_HEIGHT * 0.3;
    this.mesh.add(this.interiorLight);

    // Interior back panel (slightly lighter)
    const interiorMat = new THREE.MeshStandardMaterial({
      color: 0xc8d4e0,
      metalness: 0.2,
      roughness: 0.7,
    });
    const interior = new THREE.Mesh(
      new THREE.BoxGeometry(FREEZER_WIDTH - 0.04, FREEZER_HEIGHT - 0.04, 0.01),
      interiorMat
    );
    interior.position.set(0, FREEZER_HEIGHT / 2 + 0.04, -FREEZER_DEPTH / 2 + 0.02);
    this.mesh.add(interior);
  }

  private buildShelves(): void {
    const shelfCount = this.freezerData.shelves.length;
    // A hűtő belső tere: alj (0.04) és teteje (FREEZER_HEIGHT + 0.04)
    const bottomY = 0.08;
    const topY = FREEZER_HEIGHT - 0.02;
    const usableHeight = topY - bottomY;
    const spacing = usableHeight / shelfCount;

    for (let i = 0; i < shelfCount; i++) {
      const shelfData = this.freezerData.shelves[i];
      const shelf = new Shelf(shelfData);
      const y = bottomY + i * spacing;
      shelf.mesh.position.set(0, y, -FREEZER_DEPTH / 4);
      this.mesh.add(shelf.mesh);
      this.shelves.push(shelf);
    }
  }

  private buildLabel(): void {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;

    // Háttér lekerekített téglalappal - világoskék
    ctx.fillStyle = '#d0e4f5';
    const r = 16;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(512 - r, 0);
    ctx.quadraticCurveTo(512, 0, 512, r);
    ctx.lineTo(512, 128 - r);
    ctx.quadraticCurveTo(512, 128, 512 - r, 128);
    ctx.lineTo(r, 128);
    ctx.quadraticCurveTo(0, 128, 0, 128 - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.fill();

    // Szöveg - sötétkék
    ctx.fillStyle = '#0d3b6e';
    ctx.font = 'bold 52px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.freezerData.label, 256, 55);

    // Hőmérséklet alá
    ctx.fillStyle = 'rgba(13,59,110,0.65)';
    ctx.font = '30px sans-serif';
    ctx.fillText(`${this.freezerData.temperature}°C`, 256, 100);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true });
    this.labelSprite = new THREE.Sprite(spriteMat);
    this.labelSprite.scale.set(0.9, 0.22, 1);
    this.labelSprite.position.set(0, FREEZER_HEIGHT + 0.2, 0);
    this.mesh.add(this.labelSprite);
  }

  private buildLEDDisplay(): void {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 48;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, 128, 48);
    ctx.fillStyle = '#00ff88';
    ctx.font = 'bold 24px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${this.freezerData.temperature}°C`, 64, 32);

    const texture = new THREE.CanvasTexture(canvas);
    const mat = new THREE.MeshBasicMaterial({ map: texture });
    const display = new THREE.Mesh(new THREE.PlaneGeometry(0.12, 0.045), mat);
    display.position.set(0.25, FREEZER_HEIGHT + 0.04, FREEZER_DEPTH / 2 + 0.016);
    display.userData = { type: 'freezer', id: this.freezerData.id, freezerRef: this };
    this.mesh.add(display);
  }

  open(): Promise<void> {
    if (this.isOpen) return Promise.resolve();
    this.isOpen = true;
    this.interiorLight.intensity = 1.5;
    this.spawnParticles();
    return new Promise((resolve) => {
      tween({
        from: 0,
        to: -Math.PI / 2.2,
        duration: 800,
        easing: Easing.easeOutBack,
        onUpdate: (v) => {
          this.door.rotation.y = v;
        },
        onComplete: resolve,
      });
    });
  }

  close(): Promise<void> {
    if (!this.isOpen) return Promise.resolve();
    this.isOpen = false;
    return new Promise((resolve) => {
      tween({
        from: this.door.rotation.y,
        to: 0,
        duration: 600,
        easing: Easing.easeOutCubic,
        onUpdate: (v) => {
          this.door.rotation.y = v;
        },
        onComplete: () => {
          this.interiorLight.intensity = 0;
          // Retract all shelves
          for (const shelf of this.shelves) {
            shelf.retract();
          }
          resolve();
        },
      });
    });
  }

  private spawnParticles(): void {
    if (this.particleSystem) {
      this.mesh.remove(this.particleSystem);
      this.particleSystem.geometry.dispose();
      (this.particleSystem.material as THREE.PointsMaterial).dispose();
    }

    const count = 200;
    const positions = new Float32Array(count * 3);
    this.particleLifetimes = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * FREEZER_WIDTH;
      positions[i * 3 + 1] = Math.random() * FREEZER_HEIGHT + 0.04;
      positions[i * 3 + 2] = FREEZER_DEPTH / 2 + Math.random() * 0.3;
      this.particleLifetimes[i] = Math.random() * 2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const mat = new THREE.PointsMaterial({
      color: 0xccddff,
      size: 0.008,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.particleSystem = new THREE.Points(geo, mat);
    this.particleSystem.position.set(0, 0, 0);
    this.mesh.add(this.particleSystem);
  }

  dim(dimmed: boolean): void {
    this.isDimmed = dimmed;
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const mat = child.material as THREE.Material;
        if ('opacity' in mat) {
          (mat as any).transparent = true;
          (mat as any).opacity = dimmed ? 0.15 : (mat.userData?.originalOpacity ?? 1);
        }
      }
    });
  }

  update(time: number, delta: number): void {
    // Update particles
    if (this.particleSystem && this.particleLifetimes && this.isOpen) {
      const positions = this.particleSystem.geometry.attributes.position as THREE.BufferAttribute;
      const arr = positions.array as Float32Array;
      for (let i = 0; i < this.particleLifetimes.length; i++) {
        this.particleLifetimes[i] -= delta;
        if (this.particleLifetimes[i] <= 0) {
          // Reset particle
          arr[i * 3] = (Math.random() - 0.5) * FREEZER_WIDTH;
          arr[i * 3 + 1] = Math.random() * FREEZER_HEIGHT + 0.04;
          arr[i * 3 + 2] = FREEZER_DEPTH / 2 + 0.01;
          this.particleLifetimes[i] = 1 + Math.random() * 2;
        } else {
          // Drift outward and down
          arr[i * 3 + 1] -= delta * 0.05;
          arr[i * 3 + 2] += delta * 0.08;
          arr[i * 3] += (Math.random() - 0.5) * delta * 0.03;
        }
      }
      positions.needsUpdate = true;

      // Fade particles material
      const mat = this.particleSystem.material as THREE.PointsMaterial;
      mat.opacity = Math.max(0, mat.opacity - delta * 0.1);
      if (mat.opacity <= 0) {
        this.mesh.remove(this.particleSystem);
        this.particleSystem.geometry.dispose();
        mat.dispose();
        this.particleSystem = null;
        this.particleLifetimes = null;
      }
    }

    // Update shelf pulses
    for (const shelf of this.shelves) {
      shelf.updatePulse(time);
    }
  }
}
